import React, { useState, useEffect } from "react";
import { compose } from "recompose";
import { Table, Card, Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from "reactstrap";
import {
  TableContainer,
  Pagination,
  TableDataFetch,
  withTable,
  BulkDeleteButton,
  EditDeleteButtons,
  ViewingRecords,
} from "components/common/Tables/index";
import { Page } from "components/common";
import { formatDate, trueToYes } from "services/util";
import { Trans, useTranslation } from "react-i18next";
import DatePicker from 'react-datepicker';
import {FormGroup}  from "components/common/Forms";
import { Control, LocalForm } from "react-redux-form";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import { Link } from "react-router-dom";
import { jsonToCsv } from "pages/Xero/Reports/ExportButton";
import moment from "moment";

const currentStatusLabel = (i) => {
  if (i.is_internal_user) return "Team";
  if (i.suspended) return "Suspended";
  return i.is_active ? "Active" : "Inactive";
};

const currentStatusBadgeClass = (i) => {
  if (i.is_internal_user) return "badge-info";
  if (i.suspended) return "badge-danger";
  return i.is_active ? "badge-success" : "badge-warning";
};

const StatCard = ({ label, value, onClick }) => {
  return (
    <Card body className="px-3 py-2 mb-2">
      <div className="d-flex flex-column align-items-center">
        <div
          style={{ lineHeight: "1.5rem", fontSize: ".875rem" }}
          className="font-weight-semibold text-nowrap"
        >
          <Trans>{label}</Trans>
        </div>
        <div>
          <button
            className="btn btn-link"
            onClick={(e) => {
              e.preventDefault();
              onClick();
            }}
          >
            {value ?? 0}
          </button>
        </div>
      </div>
    </Card>
  );
};

export const UserSearch = ({
  selected,
  match,
  url = "users",
  setSelected,
  ordering,
  setOrdering,
  data,
  setData,
  filters,
  limit,
  offset,
  setOffset,
  setFilters,
  search,
  setSearch,
  isDeleteModalOpen,
  toggleDeleteModal,
  onDeleteItem,
  onDeleted,
  refreshData,
  refreshToken,
  readOnly,
  client,
}) => {

  const {t} = useTranslation()

  const [isExporting, setIsExporting] = useState(false);

  const [searchForm, setSearchForm] = useState({})
  const [isChanging, setIsChanging] = useState()
  const [companyOptions, setOptions] = useState()
  const [statusOption, setStatusOption] = useState(null)
  const [stats, setStats] = useState()

  useEffect(() => {
    (async () => {
      const res = await client.get("users/role_stats", {
        params: { role__in: "User" },
      });
      setStats(res.data);
    })();
  }, []);

  const handleInputChange = (event) => {
    setSearchForm({...searchForm, input:event.target.value})
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      applyFilters();
    }
  };

  const onCardFilter = (cardFilters) => {
    setSearchForm({})
    setStatusOption(null)
    setFilters({
      ...filters,
      date_joined__gte: undefined,
      date_joined__lte: undefined,
      search: undefined,
      is_active: undefined,
      suspended: undefined,
      is_internal_user: undefined,
      ...cardFilters,
    })
  }

  const handleStartDateChange = (date) => {
    setSearchForm({...searchForm, start_date:date})
  };

  const handleEndDateChange = (date) => {
    setSearchForm({...searchForm, end_date:date})
  };

  const handleStatusChange = (opt) => {
    setStatusOption(opt)
  }

  const applyFilters = () => {
    const {
      date_joined__gte,
      date_joined__lte,
      is_active,
      suspended,
      is_internal_user,
      trial_ends_date__gte,
      trial_ends_date__lte,
      search,
      ...next
    } = filters

    if (searchForm.input) {
      next.search = searchForm.input
    }

    if (searchForm.start_date) {
      next.date_joined__gte = moment(searchForm.start_date).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
    }
    if (searchForm.end_date) {
      next.date_joined__lte = moment(searchForm.end_date).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
    }

    switch (statusOption?.value) {
      case 'active':
        next.is_active = true
        break
      case 'inactive':
        next.is_active = false
        break
      case 'suspended':
        next.suspended = true
        break
      case 'team':
        next.is_internal_user = true
        break
      default:
        break
    }

    setFilters(next)
  }

  const clearFilters = () => {
    setSearchForm({})
    setStatusOption(null)
    const {
      date_joined__gte,
      date_joined__lte,
      trial_ends_date__gte,
      trial_ends_date__lte,
      is_active,
      suspended,
      is_internal_user,
      search,
      ...next
    } = filters;
    
    setFilters(next);
  }

  const fetchData = async () => {
    try {
      const res = await client.get("core/company/list_names");
      setOptions(res.data?.map((x) => ({ value: x.id, label: x.name })));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [isModalOpen, setisOpen] = useState(false);
  const [userDetails, setDetails] = useState()

  const closeModal = () => {
    setisOpen(false);
  };

  const onUserClick =  async (details) => { 
    setDetails(details)
    setisOpen(true)
  }

  const onChangeCompany = async (values) => {

  setIsChanging(true)

  NotificationManager.info("Changing the users company", userDetails.first_name)

  let postData = {
    ref_company_id: values?.ref_company_id,
  }

  client.post(`/users/${userDetails.id}/organisation-enrollment`, postData)
    .then(response => {
      refreshData()
        setTimeout(() => {
          setIsChanging(false)
          NotificationManager.success(t("User company was changed successfully."));
          closeModal()
        }, 1000);
    })
    .catch(error => {
      console.error("Error:", error);
      setIsChanging(false);
    });
  }

  const onExport = async () => {

    setIsExporting(true)

    let exportData = {}

    try {
      exportData = await client.get(`users`, {
        params: {
          ...filters,
          limit: 10000,
          offset: 0
        }
      }).get("data");
      setIsExporting(false)
    } catch (error) {
      setIsExporting(false)
      console.error(error);
    }

    let result = [];
    let headers = [
      t("ID"),
      t("Full Name"),
      t("Company Name"),
      t("Email"),
      t("Contact Number"),
      t("Tax Number"),
      t("Date Registered"),
      t("Trial End Date"),
      t("User Expired"),
      t("Active"),
    ];
    result.push(headers);

    for (const i of exportData.results) {
      const rowData = [
        i.id,
        i.full_name,
        `"${i.company_name || ''}"`,
        i.email,
        i.contact_number,
        i.tax_number,
        formatDate(i.date_joined),
        formatDate(i.trial_ends_date),
        trueToYes(i.trial_ended),
        trueToYes(i.is_active),
      ];

      result.push(rowData);
    }

    jsonToCsv(result, "SubscriberData");
  };

  return (
    <Page title="Users" style={{ maxWidth: '1800px', }} className="inv-table">
      {stats ? (
        <Row className="mb-2">
          <Col lg>
            <StatCard
              label="Active"
              value={stats.active}
              onClick={() => onCardFilter({ is_active: true, suspended: false, is_internal_user: false })}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Inactive"
              value={stats.inactive}
              onClick={() => onCardFilter({ is_active: false, suspended: false, is_internal_user: false })}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Suspended"
              value={stats.suspended}
              onClick={() => onCardFilter({ suspended: true })}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Team"
              value={stats.team}
              onClick={() => onCardFilter({ is_internal_user: true })}
            />
          </Col>
          <Col lg>
            <StatCard
              label="Total Users"
              value={stats.total}
              onClick={() => onCardFilter({})}
            />
          </Col>
        </Row>
      ) : null}
      <div>
        <BulkDeleteButton
          className="float-right mb-2"
          toggleModal={toggleDeleteModal}
          selected={selected}
          url={match.url}
          onDeleted={onDeleted}
          isModalOpen={isDeleteModalOpen}
          readOnly={readOnly}
        />
        <Button color="primary" className="ml-0" disabled={isExporting} onClick={onExport}>
          {isExporting ? <i className="fa fa-spin fa-spinner mr-2" /> : <i className="fas fa-file-excel mr-2" />}<Trans>{isExporting ? "Exporting" : "Export"}</Trans>
        </Button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', padding: '5px', alignItems: 'flex-end' }}>
        <FormGroup label="Search" style={{ flex: 2 }}>
          <input
            className="form-control"
            placeholder={t("Search by name, company or email")}
            type="text"
            id="searchInput"
            value={searchForm.input || ""}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
          />
        </FormGroup>
        <FormGroup label="Start Date" style={{ flex: 1 }}>
          <DatePicker
            className="form-control"
            selected={searchForm.start_date}
            placeholderText="01/01/2024"
            onChange={handleStartDateChange}
            dateFormat="YYYY-MM-DD"
          />
        </FormGroup>
        <FormGroup label="End Date" style={{ flex: 1 }}>
          <DatePicker
            className="form-control"
            selected={searchForm.end_date}
            placeholderText="01/01/2024"
            onChange={handleEndDateChange}
            dateFormat="YYYY-MM-DD"
          />
        </FormGroup>
        <FormGroup label={t("Status")} style={{ flex: 1 }}>
          <Select
            options={[
              { label: t('Active'), value: 'active' },
              { label: t('Inactive'), value: 'inactive' },
              { label: t('Suspended'), value: 'suspended' },
              { label: t('Team'), value: 'team' },
            ]}
            style={{ backgroundColor: '#F4F7FE' }}
            value={statusOption}
            onChange={handleStatusChange}
            clearable
            placeholder={t("Select status")}
          />
        </FormGroup>
        <div style={{ display: 'flex', marginLeft: 'auto' }}>
          <Button color="primary" onClick={applyFilters}>{t("Apply Filters")}</Button>
          <Button color="secondary" className="ml-2" onClick={clearFilters}>{t("Clear Filters")}</Button>
        </div>
      </div>
      <Modal isOpen={isModalOpen} toggle={closeModal}>
        <LocalForm onSubmit={onChangeCompany}>
          <ModalHeader>
            <div><Trans>User Details</Trans></div>
          </ModalHeader>
          <ModalBody className="mb-4" style={{overflowY: 'visible'}}>
              <div>
                <span><Trans>You are changing the company of the user</Trans> <span className="text-muted">{userDetails?.full_name}</span></span>
              </div>
              <Row className="mt-2">
                <Col>
                  <FormGroup className="my-2" addControlClass={false} label="Select Company">
                    <Control.text 
                      style={{ backgroundColor: '#F4F7FE' }}
                      component={Select} 
                      model=".ref_company_id" 
                      simpleValue 
                      options={companyOptions} 
                      defaultValue={userDetails?.ref_company} 
                      clearable={false}
                      // onChange={(val) => onCompanySelect(val)}
                    />
                  </FormGroup>

                  <FormGroup className="my-2" label="Selected UserID">
                    <Control.text model=".user_id" defaultValue={userDetails?.id} disabled/>
                  </FormGroup>
                  <FormGroup className="my-2" label="Selected Email">
                    <Control.text model=".user_email" defaultValue={userDetails?.email} disabled/>
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              {!isChanging ?
                <Button color="primary"><Trans>Assign</Trans></Button>
                :
                <Button color="primary" disabled><i className="fa fa-spin fa-spinner"/> <Trans>Assigning</Trans></Button>
              }
              <Button onClick={closeModal}>Close</Button>
            </ModalFooter>
          </LocalForm>
        </Modal>
      <TableContainer
        items={data.results}  
        ordering={ordering}
        onOrderingChanged={setOrdering}
        selected={selected}
        onSelectedChanged={setSelected}
      >
        <Table responsive>
          <thead> 
            <tr>
              <th select width="5%" />
              <th sortBy="first_name"><Trans>Name</Trans></th>
              <th sortBy="email"><Trans>Email</Trans></th>
              <th sortBy="company_name"><Trans>Company Name</Trans></th>
              <th sortBy="date_joined"><Trans>Registration Date</Trans></th>
              <th><Trans>Current Status</Trans></th>
              <th sortBy="email_verified"><Trans>Verified</Trans></th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.results.map((i) => (
              <tr key={i.id}>
                <td select />
                <td>{i.first_name} {i.last_name}</td>
                <td>{i.email}</td>
                <td><Link to={`/core/company-detail/${i.ref_company}`}>{i.company_name}</Link></td>
                <td>{formatDate(i.date_joined)}</td>
                <td>
                  <span className={`badge ${currentStatusBadgeClass(i)}`}>
                    {t(currentStatusLabel(i))}
                  </span>
                </td>
                <td>
                  {!!i.email_verified ? (
                    <span className="fa fa-check-circle text-success" />
                  ) : (
                    <span className="fa fa-times-circle text-danger" />
                  )}
                </td>
                <td className="text-end text-nowrap">
                  {i.xero_contact_id ? (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://go.xero.com/Contacts/View/${i.xero_contact_id}`}
                      className="btn btn-info btn-sm mr-1"
                    >xero</a>
                  ) : null}
                  <EditDeleteButtons
                    url={match.url}
                    id={i.id}
                    onDelete={onDeleteItem}
                    readOnly={readOnly}
                  />
                <Button size="sm" onClick={() => onUserClick(i)}><strong>C</strong></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      <TableDataFetch
        ordering={ordering}
        refreshToken={refreshToken}
        search={search}
        limit={limit}
        filters={filters}
        url={url}
        offset={offset}
        onDataFetched={setData}
      />  
      <ViewingRecords
        offset={offset}
        limit={limit}
        count={data.count}
        className="float-left"
      />
        <div className="float-right">
          <Pagination
            size="sm"
            count={data.count}
            limit={limit}
            offset={offset}
            onOffsetChanged={setOffset}
          />
        </div>
    </Page>
  );
};

const enhance = compose(withTable({ limit: 100, filters: { role__in: "User" } }));

export default enhance(UserSearch);
